import { z } from "zod";

/**
 * Campaign budget rules (ADP-222) — Amazon SP budget rules, created and managed
 * from the Budget Tab. A rule raises the campaign's daily budget by a percent
 * while it is in effect: on a schedule (date range or an Amazon special event,
 * daily or on chosen weekdays), or additionally only while a performance
 * condition holds.
 */
export const budgetRuleTypeSchema = z.enum(["schedule", "performance"]);
export type BudgetRuleType = z.infer<typeof budgetRuleTypeSchema>;

export const budgetRuleStateSchema = z.enum(["active", "paused"]);
export type BudgetRuleState = z.infer<typeof budgetRuleStateSchema>;

export const dayOfWeekSchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

export const budgetRuleMetricSchema = z.enum(["acos", "ctr", "cvr", "roas"]);
export type BudgetRuleMetric = z.infer<typeof budgetRuleMetricSchema>;

export const budgetRuleOperatorSchema = z.enum(["gt", "gte", "lt", "lte", "eq"]);
export type BudgetRuleOperator = z.infer<typeof budgetRuleOperatorSchema>;

const isoDate = z.string().date();

export const budgetRuleDurationSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("date_range"), startDate: isoDate, endDate: isoDate.nullable() }),
  z.object({
    type: z.literal("event"),
    eventId: z.string().min(1),
    // Read-only on Amazon; echoed back for display.
    eventName: z.string().nullable(),
    startDate: isoDate.nullable(),
    endDate: isoDate.nullable(),
  }),
]);
export type BudgetRuleDuration = z.infer<typeof budgetRuleDurationSchema>;

export const budgetRuleRecurrenceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("daily") }),
  z.object({ type: z.literal("weekly"), daysOfWeek: z.array(dayOfWeekSchema).min(1) }),
]);
export type BudgetRuleRecurrence = z.infer<typeof budgetRuleRecurrenceSchema>;

// Unit (percent vs ratio for ACOS/CTR/CVR) is unconfirmed against Amazon; the cap
// only keeps a typo from overflowing storage after Amazon already accepted it.
export const BUDGET_RULE_MAX_THRESHOLD = 1_000_000;

export const budgetRulePerformanceSchema = z.object({
  metric: budgetRuleMetricSchema,
  operator: budgetRuleOperatorSchema,
  threshold: z.number().nonnegative().max(BUDGET_RULE_MAX_THRESHOLD),
});
export type BudgetRulePerformance = z.infer<typeof budgetRulePerformanceSchema>;

export const BUDGET_RULE_MAX_INCREASE_PERCENT = 1000;

export const budgetRuleSchema = z.object({
  id: z.string().uuid(),
  amazonRuleId: z.string(),
  name: z.string(),
  ruleType: budgetRuleTypeSchema,
  state: budgetRuleStateSchema,
  /** Amazon's read-only evaluation status (e.g. ACTIVE / ARCHIVED); display only. */
  status: z.string().nullable(),
  duration: budgetRuleDurationSchema,
  recurrence: budgetRuleRecurrenceSchema,
  increasePercent: z.number(),
  performance: budgetRulePerformanceSchema.nullable(),
  createdAt: z.string().datetime({ offset: true }).nullable(),
  updatedAt: z.string().datetime({ offset: true }).nullable(),
  /**
   * False when the rule uses Amazon settings this form can't represent (e.g. an
   * intra-day window set in the Amazon console): it can still be paused or
   * removed — its Amazon definition is resent verbatim — but not edited here.
   */
  editable: z.boolean(),
  /** Campaigns this (shop-level) rule is attached to; >1 means edits affect them all. */
  campaignCount: z.number().int().nonnegative(),
  /** False when AdPilot created the rule but attaching it to this campaign failed. */
  attached: z.boolean(),
});
export type BudgetRule = z.infer<typeof budgetRuleSchema>;

/** Create / full-edit payload. Amazon has no partial update for rule details. */
export const budgetRuleInputSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    ruleType: budgetRuleTypeSchema,
    duration: z.discriminatedUnion("type", [
      z.object({ type: z.literal("date_range"), startDate: isoDate, endDate: isoDate.nullable() }),
      z.object({
        type: z.literal("event"),
        eventId: z.string().min(1),
        // Display only (from the recommendation list); Amazon keys events by id.
        eventName: z.string().nullable().optional(),
        startDate: isoDate.nullable().optional(),
        endDate: isoDate.nullable().optional(),
      }),
    ]),
    recurrence: budgetRuleRecurrenceSchema,
    increasePercent: z.number().positive().max(BUDGET_RULE_MAX_INCREASE_PERCENT).multipleOf(0.01),
    performance: budgetRulePerformanceSchema.nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.ruleType === "performance" && !value.performance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["performance"],
        message: "A performance rule needs a metric condition",
      });
    }
    if (value.ruleType === "schedule" && value.performance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["performance"],
        message: "A schedule rule has no metric condition",
      });
    }
    if (
      value.duration.type === "date_range" &&
      value.duration.endDate !== null &&
      value.duration.endDate < value.duration.startDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["duration", "endDate"],
        message: "End date must not precede start date",
      });
    }
  });
export type BudgetRuleInput = z.infer<typeof budgetRuleInputSchema>;

export const budgetRuleStateUpdateSchema = z.object({ state: budgetRuleStateSchema });

export const recommendedBudgetRuleEventSchema = z.object({
  eventId: z.string(),
  eventName: z.string().nullable(),
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  suggestedIncreasePercent: z.number().nullable(),
});
export type RecommendedBudgetRuleEvent = z.infer<typeof recommendedBudgetRuleEventSchema>;

export const campaignBudgetRulesSchema = z.object({
  /** Budget rules exist for Sponsored Products campaigns only (for now). */
  supported: z.boolean(),
  rules: z.array(budgetRuleSchema),
});
export type CampaignBudgetRules = z.infer<typeof campaignBudgetRulesSchema>;

export const budgetRuleParamsSchema = z.object({
  id: z.string().uuid(),
  ruleId: z.string().uuid(),
});
